import {
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import constants from 'src/constants';
import { userProfileApi } from 'src/api/administration/user-profile-api';
import { userApi } from 'src/api/administration/user-api';
import { fetchToken } from 'src/firebase';
import { loginStep2 } from './authenSlice';
import { getTokenData } from 'src/utils/localStorage';

const normalizeDeptId = (value: any): string =>
  String(value || '').trim();

const normalizeUserText = (value: any): string =>
  String(value || '').trim();

const normalizeDeptIds = (value: any): string[] => {
  const extractDeptId = (item: any): string => {
    if (
      typeof item === 'string' ||
      typeof item === 'number'
    ) {
      return normalizeDeptId(item);
    }

    if (item && typeof item === 'object') {
      return normalizeDeptId(
        item.id ||
          item.deptId ||
          item.depId ||
          item.value,
      );
    }

    return '';
  };

  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map(extractDeptId)
          .filter((id: string) => id.length > 0),
      ),
    );
  }

  const singleValue = extractDeptId(value);
  return singleValue ? [singleValue] : [];
};

const shouldFetchUserProfile = (
  state: RootState,
): boolean => {
  const roleId = normalizeUserText(state.userInfo.roleId);
  const username = normalizeUserText(
    state.userInfo.username,
  ).toLowerCase();
  const fullName = normalizeUserText(
    state.userInfo.fullName,
  );

  if (!roleId) {
    return true;
  }

  if (!fullName) {
    return true;
  }

  return fullName.toLowerCase() === username;
};

const resolveLeadershipDeptIdsFromPayload = (
  payload: any,
): string[] => {
  const roleId = normalizeDeptId(payload?.roleId);
  if (roleId !== constants.ROLES_KEY.LEADER) {
    return [];
  }

  const directLeadershipDeptIds = normalizeDeptIds(
    payload?.leadershipDeptIds ??
      payload?.leadershipDeptId,
  );
  if (directLeadershipDeptIds.length > 0) {
    return directLeadershipDeptIds;
  }

  const userDepartmentRoles = Array.isArray(
    payload?.userDepartmentRoles,
  )
    ? payload.userDepartmentRoles
    : [];
  if (userDepartmentRoles.length === 0) {
    return [];
  }

  const activeDeptId = normalizeDeptId(
    payload?.deptId || payload?.depId,
  );

  const normalizedActiveRole = userDepartmentRoles.find(
    (item: any) =>
      normalizeDeptId(item?.roleId) === roleId &&
      normalizeDeptId(item?.deptId || item?.depId) ===
        activeDeptId,
  );
  const fallbackActiveRole = userDepartmentRoles.find(
    (item: any) =>
      normalizeDeptId(item?.roleId) === roleId,
  );
  const targetRole =
    normalizedActiveRole || fallbackActiveRole;

  if (!targetRole) return [];

  const idsFromRole = normalizeDeptIds(
    targetRole?.leadershipDeptIds ??
      targetRole?.leadershipDeptId ??
      targetRole?.deptIds,
  );
  return idsFromRole;
};

export interface UserState {
  id: string;
  username: string;
  fullName: string;
  deptName?: string | undefined;
  avatar?: string | undefined;
  roleId?: string | undefined;
  gender?: string | undefined;
  birthDay?: Date | undefined;
  roleName?: string | undefined;
  deptId: string;
  deptIds: string[];
  userType: string;
  deptsAdmin: string[];
  leadershipDeptIds: string[];
  isMenuReportTCT?: boolean;
  delegatorUsernames: string[];
}

const initialState: UserState = {
  id: '',
  username: '',
  fullName: '',
  deptName: '',
  avatar: '',
  roleId: '',
  deptId: '',
  deptIds: [],
  userType: '',
  deptsAdmin: [],
  leadershipDeptIds: [],
  delegatorUsernames: [],
};

export const getUserInfo = createAsyncThunk(
  'auth/info',
  async (arg, { getState }) => {
    const state = getState() as RootState;
    if (shouldFetchUserProfile(state)) {
      const userProfile = await userProfileApi.getCurrentUser();
      let tokenData: any = null;
      try {
        const rawTokenData = getTokenData();
        if (
          rawTokenData &&
          rawTokenData !== 'undefined'
        ) {
          tokenData = JSON.parse(rawTokenData);
        }
      } catch (error) {
        tokenData = null;
      }

      const selectedDeptId = String(
        tokenData?.deptId || '',
      ).trim();
      const selectedDeptName = String(
        tokenData?.deptName || '',
      ).trim();
      const selectedRoleId = String(
        tokenData?.roleId || '',
      ).trim();
      const selectedRoleName = String(
        tokenData?.roleName || '',
      ).trim();

      const mergedUserProfile = {
        ...userProfile,
      } as any;
      if (selectedDeptId) {
        mergedUserProfile.deptId = selectedDeptId;
      }
      if (selectedDeptName) {
        mergedUserProfile.deptName = selectedDeptName;
      }
      if (selectedRoleId) {
        mergedUserProfile.roleId = selectedRoleId;
      }
      if (selectedRoleName) {
        mergedUserProfile.roleName = selectedRoleName;
      }
      const tokenDeptIds = normalizeDeptIds(
        tokenData?.deptIds,
      );
      if (tokenDeptIds.length > 0) {
        mergedUserProfile.deptIds = tokenDeptIds;
      }
      if (
        Array.isArray(tokenData?.userDepartmentRoles)
      ) {
        mergedUserProfile.userDepartmentRoles =
          tokenData.userDepartmentRoles;
      }
      if (!mergedUserProfile.userType && selectedRoleId) {
        mergedUserProfile.userType = selectedRoleId;
      }

      let leadershipDeptIds =
        resolveLeadershipDeptIdsFromPayload({
          ...mergedUserProfile,
          roleId:
            selectedRoleId || mergedUserProfile.roleId,
          deptId:
            selectedDeptId || mergedUserProfile.deptId,
        });

      if (
        leadershipDeptIds.length === 0 &&
        normalizeDeptId(
          selectedRoleId || mergedUserProfile.roleId,
        ) === constants.ROLES_KEY.LEADER
      ) {
        const userId = normalizeDeptId(
          mergedUserProfile?.id || tokenData?.id,
        );
        if (userId) {
          try {
            const deptRoles =
              await userApi.getDeptRoles(userId);
            leadershipDeptIds =
              resolveLeadershipDeptIdsFromPayload({
                roleId:
                  selectedRoleId ||
                  mergedUserProfile.roleId,
                deptId:
                  selectedDeptId ||
                  mergedUserProfile.deptId,
                userDepartmentRoles: deptRoles,
              });
            mergedUserProfile.userDepartmentRoles =
              deptRoles;
          } catch (error) {
            leadershipDeptIds = [];
          }
        }
      }

      if (leadershipDeptIds.length > 0) {
        mergedUserProfile.leadershipDeptIds =
          leadershipDeptIds;
      }
      // luan.nguyenxuan - 04/02/2026: off fetch token firebase
      // await fetchToken((obj: any) => {});
      return mergedUserProfile;
    }
  },
);

export const userInfoSlice = createSlice({
  name: 'userInfo',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(
      getUserInfo.fulfilled,
      (state, action: any) => {
        if (action.payload) {
          const deptId = String(
            action.payload.deptId || '',
          );
          const profileDeptIds = Array.isArray(
            action.payload.deptIds,
          )
            ? action.payload.deptIds.filter(
                (item: any): item is string =>
                  typeof item === 'string' &&
                  item.length > 0,
              )
            : [];
          const deptIds: string[] = Array.from(
            new Set<string>(
              deptId
                ? [deptId, ...profileDeptIds]
                : profileDeptIds,
            ),
          );
          const leadershipDeptIds =
            resolveLeadershipDeptIdsFromPayload(
              action.payload,
            );
          state.id = action.payload.id || state.id;
          state.username = action.payload.username;
          state.fullName =
            action.payload.fullName ||
            action.payload.username;
          state.deptName = action.payload.deptName;
          state.avatar = action.payload.avatar;
          state.roleId = action.payload.roleId;
          state.gender = action.payload.gender;
          state.birthDay = action.payload.birthDay;
          state.roleName = action.payload.roleName;
          state.deptId = deptId;
          state.deptIds = deptIds;
          state.userType =
            action.payload.userType ||
            action.payload.roleId ||
            '';
          state.deptsAdmin = deptIds;
          state.leadershipDeptIds =
            leadershipDeptIds;
          state.isMenuReportTCT =
            action.payload.isMenuReportTCT;
          state.delegatorUsernames =
            action.payload.delegatorUsernames || [];
        }
      },
    );
    builder.addCase(
      loginStep2.fulfilled,
      (state, action: any) => {
        if (action.payload && action.payload.user) {
          const user = action.payload.user;
          const deptId = String(user.deptId || '');
          const loginDeptIds = Array.isArray(user.deptIds)
            ? user.deptIds.filter(
                (item: any): item is string =>
                  typeof item === 'string' &&
                  item.length > 0,
              )
            : [];
          const deptIds: string[] = Array.from(
            new Set<string>(
              deptId
                ? [deptId, ...loginDeptIds]
                : loginDeptIds,
            ),
          );
          const leadershipDeptIds =
            resolveLeadershipDeptIdsFromPayload(user);
          state.id = user.id || state.id;
          state.username = user.username;
          // Use username as fallback for fullName if API doesn't return it
          state.fullName =
            user.fullName ||
            user.username;
          state.deptId = deptId;
          state.deptName =
            user.deptName || state.deptName;
          state.avatar = user.avatar || '';
          state.roleId =
            user.roleId || state.roleId || '';
          state.roleName =
            user.roleName || state.roleName;
          state.deptIds = deptIds;
          state.userType =
            user.userType ||
            user.roleId ||
            state.userType ||
            '';
          state.deptsAdmin = deptIds;
          state.leadershipDeptIds =
            leadershipDeptIds;
          state.isMenuReportTCT =
            user.isMenuReportTCT ??
            state.isMenuReportTCT;
          state.delegatorUsernames =
            user.delegatorUsernames ||
            state.delegatorUsernames ||
            [];
        }
      },
    );
  },
});

export const {} = userInfoSlice.actions;

export const selectUserInfo = (state: RootState) =>
  state.userInfo;

export default userInfoSlice.reducer;
